import { ColumnFiltersState, Table } from '@tanstack/react-table';
import equal from 'fast-deep-equal';

import { ColumnDef } from '../types';

type FilterResult = {
    clientSideFilters: Record<string, string[]>;
    serverSideFilters: Record<string, string[]>;
};

interface BuildFilters<TData> {
    table: Table<TData>;
    filters: ColumnFiltersState;
}

const buildFilters = <TData>({
    filters,
    table
}: BuildFilters<TData>): FilterResult => {
    const calculatedFilters: FilterResult = {
        clientSideFilters: {},
        serverSideFilters: {}
    };

    const paramFilters = filtersToParameters(filters);
    const leafColumns = table.getAllLeafColumns();

    // Iterate over each filter
    Object.keys(paramFilters).forEach((columnId) => {
        // Get the clientSideFiltering flag from the column's meta, if it exists
        const value = paramFilters[columnId];
        const columnDef = leafColumns.find(({ id }) => id === columnId)
            ?.columnDef as ColumnDef<TData, unknown>;
        const meta = columnDef?.meta;
        const clientSideFiltering = meta?.filter?.clientSideFiltering;

        const newFilter: Record<string, string[]> = {
            [columnId]: value
        };

        // Check if the filter has a value
        if (clientSideFiltering) {
            // If it does, add it to the client-side filters
            calculatedFilters.clientSideFilters[columnId] = newFilter[columnId];
        } else {
            // If it doesn't, add it to the server-side filters
            calculatedFilters.serverSideFilters[columnId] = newFilter[columnId];
        }
    });

    return calculatedFilters;
};

/**
 * Handles changes in table filters and updates parameters accordingly.
 *
 * @param props
 * @param {() => ColumnFiltersState} props.filterFn - Function that returns the current column filters.
 * @param {Record<string, any>} props.parameters - Current parameters object.
 * @param {Table<any>} props.table - The table instance.
 * @param {React.Dispatch<React.SetStateAction<Record<string, any>>>} props.setParameters - Function to update parameters.
 * @param {React.Dispatch<React.SetStateAction<ColumnFiltersState>>} props.setClientSideFilters - Function to update column filters.
 * @param {React.Dispatch<React.SetStateAction<ColumnFiltersState>>} props.setColumnFilters - Function to update column filters.
 * @param {Record<string, string | string[] | number | boolean>} props.baseParameters - Base parameters object to reset to when filters are cleared.
 */
export function filterChangeHandler<TData>({
    baseParameters,
    filterFn,
    setClientSideFilters,
    setColumnFilters,
    setParameters,
    table
}: {
    baseParameters: Record<string, string | string[] | number | boolean>;
    filterFn: () => ColumnFiltersState;
    table: Table<TData>;
    setParameters: React.Dispatch<
        React.SetStateAction<
            Record<string, string | string | string[] | number | boolean>
        >
    >;
    setClientSideFilters: React.Dispatch<
        React.SetStateAction<Record<string, string[]>>
    >;
    setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
}) {
    // Get the filters' change
    const filters = filterFn();

    // Remove any filter of the type { id: classified, value: [] }
    const notEmptyFilters = filters.filter(
        (filter) => (filter?.value as unknown as string[]).length > 0
    );

    const { clientSideFilters, serverSideFilters } = buildFilters({
        filters: notEmptyFilters || [],
        table
    });

    setClientSideFilters({ ...clientSideFilters });
    // check previous parameters and only update if there is a diff
    setParameters((previousParams) => {
        if (
            equal(previousParams, { ...baseParameters, ...serverSideFilters })
        ) {
            return previousParams;
        }
        return { ...baseParameters, ...serverSideFilters };
    });
    // Update column filters state
    setColumnFilters(notEmptyFilters);
}

/**
 * Converts an array of filter objects to a parameters object.
 *
 * @param {ColumnFiltersState} filters - Array of filter objects.
 * @returns {Record<string, string[]>} Object with filter ids as keys and values as values.
 */
export function filtersToParameters(
    filters: ColumnFiltersState
): Record<string, string[]> {
    return Object.fromEntries(
        filters.map((item) => [item.id, item.value as string[]])
    );
}
